import { IPageConfiguration } from '../../../domain/conf/page'
import { DOMUtils } from '../../../utils/dom'
import { IKlangooSensitive } from '../../common/nlp/klangoo-config'
import { IKlangooNegativeKeywords } from '../../common/nlp/klangoo-config'
import { PageType } from '../../common/page-types'
import { AdTargetLookup } from './lookup'
import { IAdTargetStrategyHandler } from './strategy'
import { AdTarget, IAdTargets } from './target'

interface IWindow {
    I10C: any
}

declare let window: IWindow

/**
 * Targeting keys as defined by Ad Ops. Please be vigilant in minimizing additions (be aware of redundancy etc.)
 * DO NOT repeat these key definitions elsewhere (extract if multiple targeters are required).
 */
export enum AdTargetKeys {
    ARTICLE = 'aid',
    ARTICLE_SOURCE = 'asrc',
    AUTHOR = 'author',
    CHANNEL = 'ck',
    COMMUNITY = 'comm',
    ENTITY = 'et',
    IMPRESSION = 'imp',
    LOCATION = 'loc',
    PAGE = 'page',
    PARTNER = 'prtn',
    OPT_OUT_POL = 'no_pol',
    PROPERTY = 'pr',
    REFERRER = 'r',
    REFRESH = 'rc',
    SENSITIVE = 'sensitive',
    SENSITIVE_BRAND = 'scb',
    SENSITIVE_TOPIC = 'sct',
    NEGATIVE = 'negative',
    NEGATIVE_BRAND ='nkb',
    SHIELD = 'shield',
    SUBCHANNEL = 'sck',
    TAG = 'tag',
    TEST = 'test', // NOTE: should be `yes` unless prod!
    TYPE = 'type'
}

export interface IPageTargetingConfig {
    network?: string  // TODO: remove after removal from WP and DCS
    property?: string
    ancestry?: string[]
    articleId?: string
    writers?: IPageConfiguration['writers']
    entity?: string
    optOutPoliticalAds?: boolean
    originId?: string
    refreshCount?: number
    sensitive?: IKlangooSensitive
    negative?: IKlangooNegativeKeywords[]
    type?: PageType
}

export class DefaultAdTargetingStrategy implements IAdTargetStrategyHandler {
    public default = {
        loc: 'top',
        page: PageType.INDEX
    }

    protected lookup: AdTargetLookup
    protected pageTargets: AdTarget[] = []
    protected firstConfig: IPageTargetingConfig

    constructor(lookup: any) {
        this.lookup = new AdTargetLookup(lookup)
    }

    /**
     * Determines ad-level targeting. Typical use is setting `loc` when multiple of same size.
     * Additional targeting is rarely used, but supported.
     *
     * @param el Ad element
     */
    public ad(custom: IAdTargets, loc?: string): AdTarget[] {
        const targets: AdTarget[] = []

        // set default location
        if (!(custom && custom[AdTargetKeys.LOCATION])) {
            targets.push(new AdTarget(AdTargetKeys.LOCATION, loc || this.default.loc))
        }

        Object.entries(custom || {}).forEach((_) => targets.push(new AdTarget(_[0], _[1])))

        return targets
    }

    /**
     * Determines page-level targeting.
     */
    public page(conf: IPageTargetingConfig): AdTarget[] {
        this.pageTargets = []
        if(!this.firstConfig) {
            this.firstConfig = conf
        }
        // targets using config data
        this.targetArticle(conf.articleId || this.firstConfig.articleId, conf.originId || this.firstConfig.originId)
        this.targetWriters(conf.writers || this.firstConfig.writers)
        this.targetOptOut(conf.optOutPoliticalAds|| this.firstConfig.optOutPoliticalAds)
        this.targetPageType(conf.type || this.firstConfig.type)
        this.targetProperty(conf.property || this.firstConfig.property)
        this.targetRefresh(conf.refreshCount || this.firstConfig.refreshCount)
        this.targetSensitive(conf.sensitive || this.firstConfig.sensitive)
        this.targetNegative(conf.negative || this.firstConfig.negative)

        // targets using env/lookup data
        this.targetArticleSource(conf.articleId || this.firstConfig.articleId)
        this.targetCommunity()
        this.targetEntity()
        this.targetPartner()
        this.targetReferrer()
        this.targetShield()

        // targets using logic based on ancestry and type
        this.targetWithAncestry(conf.ancestry || this.firstConfig.ancestry, conf.type || this.firstConfig.type)

        // `test` - should be 'yes' unless prod!
        if (__NON_PROD) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.TEST, 'yes'))
        }

        return this.pageTargets
    }

    /**
     * Reset Page Targets Loaded
     */
    public resetPageTargets() {
        this.pageTargets = []
        delete this.firstConfig
    }

    /**
     * Article - from page conf; array of article and origin ids.
     */
    protected targetArticle(articleId: string, originId: string) {
        if (articleId) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ARTICLE, [articleId, originId]))
        }
    }

    /**
     * Article Source - lookup by shortcode from canonical url.
     */
    protected targetArticleSource(articleId: string) {
        const canonicalUrl =  document.querySelector("link[rel='canonical']") ? DOMUtils.getRelUrl('canonical') : ''
        const articleSource = this.lookup.byShortcode(canonicalUrl)

        if (canonicalUrl !== '' && articleSource && articleId) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ARTICLE_SOURCE, articleSource))
        }
    }

    /**
     * Authors - from page conf; array of author display names.
     */
    protected targetWriters(writers: IPageConfiguration['writers']) {
        if (writers && writers.main && writers.main.length) {
            this.pageTargets.push(new AdTarget(
                AdTargetKeys.AUTHOR, writers.main.map((_) => typeof _ === 'object' ? _.name : _)))
        }
    }

    /**
     * Community - lookup by host.
     * @todo: would be nice if this could accept `1` rather than `y`
     */
    protected targetCommunity() {
        const comm = this.lookup.byHost(location.host, AdTargetKeys.COMMUNITY)
        if (comm) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.COMMUNITY, comm))
        }
    }

    /**
     * Entity - lookup by host; e.g. `sm` (Sun Media).
     */
    protected targetEntity() {
        const entity = this.lookup.byHost(location.host, AdTargetKeys.ENTITY)
        if (entity) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ENTITY, entity))
        }
    }

    /**
     * Page - from page conf; e.g. `search`.
     */
    protected targetPageType(type: string) {
        // category pages target using page=index
        if (type === PageType.CATEGORY) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PAGE, PageType.INDEX))

        } else {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PAGE, type || this.default.page))
        }
    }

    /**
     * Property - from page conf; e.g. `np`.
     * @todo: add to lookup
     * @todo: SHOULD BE DOMAIN NOT LOOKUP
     */
    protected targetProperty(property: string) {
        if (property) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PROPERTY, property))
        }
    }

    /**
     * Partner - lookup by host; e.g. `adperfect`.
     */
    protected targetPartner() {
        const partner = this.lookup.byHost(location.host, AdTargetKeys.PARTNER)
        if (partner) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PARTNER, partner))
        }
    }

    /**
     * Referrer - fetched from `document`.
     */
    protected targetReferrer() {
        if (document.referrer) {
            const domain = document.referrer.replace(/^.*\/\/([^\/]+).*$/, '$1')
            this.pageTargets.push(new AdTarget(AdTargetKeys.REFERRER, domain))
        }
    }

    /**
     * Refresh count.
     */
    protected targetRefresh(count: number) {
        const counter = (count === undefined) ? 0 : count
        this.pageTargets.push(new AdTarget(AdTargetKeys.REFRESH, String(counter)))
    }

    /**
     * Sensitive content.
     */
    protected targetSensitive(sensitive: IKlangooSensitive) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.SENSITIVE, sensitive ? 'y' : 'n'))

        if (sensitive) {
            if (sensitive.brands && sensitive.brands.length) {
                this.pageTargets.push(new AdTarget(
                    AdTargetKeys.SENSITIVE_BRAND,
                    sensitive.brands.map(({ token }) => token.toLowerCase() )))
            }

            if (sensitive.categories && sensitive.categories.length) {
                this.pageTargets.push(new AdTarget(
                    AdTargetKeys.SENSITIVE_TOPIC,
                    sensitive.categories.map(({ name }) => name.toLowerCase() )))
            }
        }
    }

    /**
     * Negative Keywords.
     */
    protected targetNegative(negatives: IKlangooNegativeKeywords[]) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.NEGATIVE, negatives && negatives.length > 0 ? 'y' : 'n'))

        if (negatives) {
            const brand: string [] = negatives.map((negative) => {
                if (negative.brand && negative.brand.length) {
                    return negative.brand
                }
                return ''
            })
            if (brand && brand.length) {
                this.pageTargets.push(new AdTarget(
                AdTargetKeys.NEGATIVE_BRAND,
                brand))
            }
        }
    }

    /**
     * Shield - for 'Index Exchange' (?)
     * @todo: explain
     */
    protected targetShield() {
        if (window.I10C && window.I10C.Morph) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.SHIELD, `1`))
        }
    }

    /**
     * Handles targeting involving ancestry and type:
     * Channel - usually top-most ancestor; e.g. `news`.
     * Sub-channel - array of ancestor slugs.
     * Impression - usually last-most ancestor; `index` on home pages
     */
    protected targetWithAncestry(ancestry: string[], type: PageType) {

        // has heirarchy (typically categories)
        if (ancestry && ancestry.length) {

            // channel key is top-most ancestor
            this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, ancestry[0]))

            // remaining ancestors are sub-category keys
            if (ancestry.length > 1) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.SUBCHANNEL, ancestry.slice(1)))
            }

            // no impression on story pages
            if (type !== PageType.STORY) {
                // impression is last-most ancestor
                this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, [...ancestry].pop()))
            }

        } else {

            // home page has 'index' as ck and imp
            if (type === PageType.INDEX) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, PageType.INDEX))
                this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, PageType.INDEX))
            }
        }
    }

    /**
     * OptOut - currently just for political ads
     */
    protected targetOptOut(optOutPol: boolean) {
        if (optOutPol) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.OPT_OUT_POL, 'true'))
        }
    }
}
